import { PersonType, RentalType } from "@/shared/types/enums";
import { z } from "zod";
import { CNPJ_LENGTH, CPF_LENGTH, cleanDocument } from "@/shared/lib/document";

const newClientSchema = z
  .object({
    name: z.string().min(1),
    document: z.string().min(11),
    personType: z.nativeEnum(PersonType),
    gender: z.enum(["M", "F"]).or(z.literal("")).optional(),
    phone: z.string().min(10),
    email: z.string().optional(),
    street: z.string().min(1),
    number: z.string().min(1).max(10),
    complement: z.string(),
    neighborhood: z.string().min(1),
    state: z.string().length(2),
    postalCode: z.string().min(8),
  })
  .superRefine((data, ctx) => {
    const document = cleanDocument(data.document);
    const isJuridica = data.personType === PersonType.JURIDICA;
    if (
      isJuridica
        ? document.length !== CNPJ_LENGTH
        : document.length !== CPF_LENGTH
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["document"],
        message: isJuridica ? "CNPJ inválido" : "CPF inválido",
      });
    }
    if (!isJuridica && !data.gender) {
      ctx.addIssue({
        code: "custom",
        path: ["gender"],
        message: "Gênero obrigatório",
      });
    }
  });

const newContractSchema = z.object({
  contractNumber: z.string().min(1),
  contractImage: z.string().optional(),
});

const paymentSchema = z.object({
  paymentMethod: z.enum(["PIX", "CREDIT_CARD", "DEBIT_CARD", "CASH"]),
  paidValue: z.number().positive("Valor do pagamento deve ser maior que zero"),
  installments: z.number().int().min(1).max(12).optional(),
});

const gownItemSchema = z.object({
  productId: z.number().int().positive(),
  quantity: z.number().int().positive(),
});

export const rentalCreateSchema = z
  .object({
    rentalType: z.enum([RentalType.SUIT, RentalType.GOWN]),
    sellerId: z.number().int().positive().nullable(),
    hasShoes: z.boolean(),
    shoesPairQuantity: z.number().int().min(0),
    totalValue: z.number().positive("Valor total obrigatório"),
    payments: z.array(paymentSchema),
    expectedOutAt: z.string().min(1, "Data de retirada obrigatória"),
    expectedInAt: z.string().min(1, "Data de devolução obrigatória"),

    // Campos exclusivos do fluxo ADMIN (/alugueis/novo-admin) — o wizard do
    // USER nunca os exibe e o builder de payload dele os ignora. Ficam aqui
    // para os dois fluxos compartilharem as mesmas etapas sem um segundo
    // schema paralelo. Ver `showRetroactive` em RentalCreateWizard.
    isRetroactive: z.boolean().optional(),
    outAt: z.string().optional(),
    inAt: z.string().optional(),
    // O backend valida @Size(max = 200) nos DOIS endpoints (RentalRequestDTO
    // do admin e CreateRentalRequestDTO do user) — sem este limite o 400 só
    // aparece no submit.
    description: z.string().max(200, "Máximo 200 caracteres"),
    emitInvoice: z.boolean(),
    invoiceObservation: z.string().max(2000, "Máximo 2000 caracteres"),

    selectedClientId: z.number().int().positive().nullable(),
    newClient: newClientSchema.nullable(),

    selectedContractId: z.number().int().positive().nullable(),
    newContract: newContractSchema.nullable(),

    parentContractId: z.number().int().positive().nullable(),

    stockIds: z.array(z.number().int().positive()),
    gownItems: z.array(gownItemSchema),
    tagIds: z.array(z.number().int().positive()),
  })
  .superRefine((data, ctx) => {
    if (!data.sellerId) {
      ctx.addIssue({
        code: "custom",
        path: ["sellerId"],
        message: "Selecione um vendedor",
      });
    }
    if (
      data.rentalType === "SUIT" &&
      data.hasShoes &&
      data.shoesPairQuantity < 1
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["shoesPairQuantity"],
        message: "Informe a quantidade de pares",
      });
    }
    if (
      data.expectedOutAt &&
      data.expectedInAt &&
      data.expectedInAt < data.expectedOutAt
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["expectedInAt"],
        message: "Devolução deve ser depois da retirada",
      });
    }
    if (!data.selectedClientId && !data.newClient) {
      ctx.addIssue({
        code: "custom",
        path: ["selectedClientId"],
        message: "Selecione ou cadastre um cliente",
      });
    }
    if (!data.selectedContractId && !data.newContract) {
      ctx.addIssue({
        code: "custom",
        path: ["selectedContractId"],
        message: "Selecione ou cadastre um contrato",
      });
    }
    // Só o fluxo ADMIN marca retroativo; no USER `isRetroactive` é sempre
    // undefined e estas checagens não disparam.
    if (data.isRetroactive && !data.outAt) {
      ctx.addIssue({
        code: "custom",
        path: ["outAt"],
        message: "Data efetiva de saída é obrigatória",
      });
    }
    if (data.isRetroactive && !data.inAt) {
      ctx.addIssue({
        code: "custom",
        path: ["inAt"],
        message: "Data efetiva de retorno é obrigatória",
      });
    }
    if (
      data.isRetroactive &&
      data.outAt &&
      data.inAt &&
      data.inAt < data.outAt
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["inAt"],
        message: "Retorno efetivo deve ser depois da saída efetiva",
      });
    }
  });

export type RentalCreateFormSchema = z.infer<typeof rentalCreateSchema>;
